import styled from "styled-components"

import { Text } from "@minuthq/meatball-ui-react"
import { Badge } from "@minuthq/meatball-ui-react"

import { SearchFilter } from "src/components/Filter/SearchFilter"
import { ParadiseOrdersFilters } from "src/components/Paradise/ParadiseOrders/ParadiseOrdersFilter"
import { OrderStatusBadge } from "src/components/Paradise/ParadiseOrders/ParadiseOrderStatusBadge"
import {
  TParadiseOrdersFilters,
  TSortedParadiseOrdersFilters,
} from "src/components/Paradise/ParadiseOrders/useParadiseOrdersFilter"
import { ParadisePager } from "src/components/Paradise/ParadisePager"
import { ParadiseSelectShippingCountriesFilter } from "src/components/Paradise/ParadiseSelectShippingCountriesFilter"
import { ParadiseSelectUsersByEmail } from "src/components/Paradise/ParadiseSelectUsersByEmail"
import { ParadiseTable } from "src/components/Paradise/ParadiseTable"
import { TopWrapper } from "src/components/Paradise/sharedStyles"
import { chargebeeInvoiceUrl } from "src/constants/hrefs"
import { getCountryDisplayName } from "src/data/countries/countryUtil"
import { prettifyApiFieldValue } from "src/data/orders/orderUtils"
import { parseParadiseCountryCodesField } from "src/data/paradise/paradiseOrders/logic/paradiseOrderLogic"
import { TParadiseOrder } from "src/data/paradise/paradiseOrders/paradiseOrderTypes"
import { TParadiseUser } from "src/data/paradise/paradiseUsers/types/paradiseUserQueryTypes"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { TSetSearchParamsProp } from "src/router/useSearchParams"
import { DropdownMultiSelect } from "src/ui/DropdownSelect/DropdownMultiSelect"
import { ColumnPopoverWrapper } from "src/ui/GridTable/useTableColumns/tableColumnStyles"
import {
  TableColumn,
  TTableSort,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import FilterIcon from "src/ui/icons/calibrating.svg"
import { ExternalLink } from "src/ui/Link/ExternalLink"
import { InternalLink } from "src/ui/Link/InternalLink"
import { formatUtcDate } from "src/utils/l10n"
import { Maybe } from "src/utils/tsUtil"

export function ParadiseOrdersTable({
  orders,
  users,
  filter,
  setFilter,
  sortedFilters,
  sort,
  setSort,
  isLoadingMore,
  isLoading,
  error,
  offset,
  limit,
  setOffset,
  totalCount,
  disableColumnHiding,
  tableStorageKey,
  hiddenColumns,
}: {
  orders: TParadiseOrder[]
  users?: TParadiseUser[]
  filter: TParadiseOrdersFilters
  setFilter: TSetSearchParamsProp<TParadiseOrdersFilters>
  sortedFilters: TSortedParadiseOrdersFilters
  sort?: Maybe<TTableSort>
  setSort?: (sort: TTableSort) => void
  isLoadingMore?: boolean
  isLoading?: boolean
  error?: {
    hasError: boolean
    title: React.ReactNode
    description?: React.ReactNode
  }
  offset: number
  limit: number
  setOffset: (offset: number) => void
  totalCount?: number
  disableColumnHiding?: boolean
  tableStorageKey?: string
  hiddenColumns?: (keyof TParadiseOrder)[]
}) {
  const { navigate } = useRouter()

  const tableColumns: TableColumn<TParadiseOrder, keyof TParadiseOrder>[] = [
    {
      label: "Order number",
      value: "order_number",
      columnWidth: "min-content",
      renderCell: (data) => (
        <div>
          <MultiItemGrid>
            <InternalLink
              to={Routes.ParadiseOrder.location(data.order_number).pathname}
            >
              {data.order_number}
            </InternalLink>
            {data.partial_fulfillment && (
              <Badge color="info" size="x-small">
                Partial fulfillment
              </Badge>
            )}
          </MultiItemGrid>
        </div>
      ),
      popoverContent: (
        <ColumnPopoverWrapper>
          <SearchFilter
            initialValue={filter.orders_order_number ?? undefined}
            placeholder="Search by order number"
            onChange={(v) => setFilter("orders_order_number", v)}
          />
          <Text variant="nano" color="secondary">
            Full order id is required to find a match
          </Text>
        </ColumnPopoverWrapper>
      ),
      popoverIcon: FilterIcon,
      disabled: true,
    },
    {
      label: "Created at",
      value: "created_at",
      columnWidth: "min-content",
      renderCell: (data) => (
        <div>{formatUtcDate({ date: data.created_at })}</div>
      ),
      enableSort: true,
    },

    {
      label: "User",
      value: "user_id",
      columnWidth: "min-content",
      disableClickPropagation: true,
      renderCell: (data) => {
        const user = users?.find((u) => u.id === data.user_id)

        return (
          <div>
            {data.user_id && (
              <InternalLink
                to={Routes.ParadiseUser.location(data.user_id).pathname}
              >
                {user?.email || data.user_id}
              </InternalLink>
            )}
          </div>
        )
      },
      popoverContent: (
        <ColumnPopoverWrapper>
          <ParadiseSelectUsersByEmail
            selectedUserIds={filter.orders_user_ids ?? []}
            onChange={(userIds) => setFilter("orders_user_ids", userIds)}
          />
        </ColumnPopoverWrapper>
      ),
      popoverIcon: FilterIcon,
    },
    {
      label: "Status",
      value: "status",
      columnWidth: "min-content",
      renderCell: (data) => (
        <Block>
          <OrderStatusBadge status={data.status} />
        </Block>
      ),
      enableSort: true,
    },
    {
      label: "Shipping country",
      value: "shipping_address",
      columnWidth: "min-content",
      renderCell: (data) => (
        <div>
          {getCountryDisplayName({
            countryCode: data.shipping_address.country,
            locale: "en",
          })}
        </div>
      ),
      popoverContent: (
        <ParadiseSelectShippingCountriesFilter
          selectedCountries={
            parseParadiseCountryCodesField(filter.orders_country_codes) ?? []
          }
          onChange={(countries) => setFilter("orders_country_codes", countries)}
        />
      ),
      popoverIcon: FilterIcon,
    },
    {
      label: "Total",
      value: "total_price",
      columnWidth: "min-content",
      renderCell: (data) => <div>{data.total_price.formatted_amount}</div>,
    },
    {
      label: "Items",
      value: "line_items",
      columnWidth: "min-content",
      renderCell: (data) => {
        const itemsCount = data.line_items.reduce(
          (acc, lineitem) => acc + lineitem.quantity,
          0
        )

        return itemsCount === 1 ? (
          <div>{itemsCount} item</div>
        ) : (
          <div>{itemsCount} items</div>
        )
      },
    },
    {
      label: "Tracking url",
      value: "tracking_url",
      columnWidth: "min-content",
      renderCell: (data) =>
        data.tracking_url ? (
          <div>
            <ExternalLink
              href={data.tracking_url}
              showExternalIcon={false}
              openInNewTab
            >
              View tracking
            </ExternalLink>
          </div>
        ) : (
          <div></div>
        ),
    },
    {
      label: "Tracking reference",
      value: "tracking_reference",
      columnWidth: "min-content",
      renderCell: (data) => <div>{data.tracking_reference}</div>,
    },
    {
      label: "Shipping method",
      value: "shipping_line_item",
      columnWidth: "min-content",
      renderCell: (data) => <div>{data.shipping_line_item?.name}</div>,
    },
    {
      label: "Invoice",
      value: "invoice_id",
      columnWidth: "min-content",
      renderCell: (data) => (
        <div>
          {data.invoice_id && (
            <ExternalLink href={chargebeeInvoiceUrl(data.invoice_id)}>
              {data.invoice_id}
            </ExternalLink>
          )}
        </div>
      ),
    },
    {
      label: "Warehouse",
      value: "assigned_warehouse",
      columnWidth: "min-content",
      renderCell: (data) => (
        <div>{prettifyApiFieldValue(data.assigned_warehouse)}</div>
      ),
    },
    {
      label: "Legal entity",
      value: "payment_legal_entity",
      columnWidth: "min-content",
      renderCell: (data) => (
        <div>
          <MultiItemGrid>
            {prettifyApiFieldValue(data.payment_legal_entity ?? "")}
            {data.is_inter_entity_shipment && (
              <Badge color="info" size="x-small">
                Inter-entity shipment
              </Badge>
            )}
          </MultiItemGrid>
        </div>
      ),
    },
    {
      label: "Origin",
      value: "origin",
      columnWidth: "min-content",
      renderCell: (data) => (
        <div>{prettifyApiFieldValue(data.origin.name)}</div>
      ),
    },
  ]

  const {
    headerElements,
    interactiveColumns,
    interactiveVisibleColumns,
    rows,
    updateColumnVisibility,
    templateColumns,
  } = useTableColumns<TParadiseOrder>({
    columns: tableColumns.map((c) => ({
      ...c,
      hidden: hiddenColumns?.includes(c.value),
    })),
    data: orders,
    sort,
    onSortChange: setSort,
    options: {
      localStorageKey: tableStorageKey,
      disableColumnHiding,
    },
  })

  return (
    <div>
      <TopWrapper>
        <ParadiseOrdersFilters
          setFilter={setFilter}
          sortedFilters={sortedFilters}
        />

        {!disableColumnHiding && (
          <DropdownMultiSelect
            label="Columns"
            selectedValues={interactiveVisibleColumns.map((c) => c.value)}
            options={interactiveColumns}
            onChange={({ checked, option }) => {
              updateColumnVisibility(option.value, !checked)
            }}
          />
        )}
      </TopWrapper>
      <ParadiseTable
        rows={rows ?? []}
        templateColumns={templateColumns}
        header={headerElements}
        onRowClick={(index) => {
          const order = orders[index]
          if (order) {
            navigate(Routes.ParadiseOrder.location(order.order_number))
          }
        }}
        error={error}
        isLoadingMore={isLoadingMore}
        isLoading={isLoading}
      />

      <ParadisePager
        limit={limit}
        offset={offset}
        setOffset={setOffset}
        totalCount={totalCount}
      />
    </div>
  )
}

const Block = styled.div`
  display: block;
`

const MultiItemGrid = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1ch;
`
